import { useEffect, useRef } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useParams } from 'react-router-dom'
import { useMenuStore } from '../../core/stores/menuStore'
import { useCartStore } from '../../core/stores/cartStore'
import { useBusinessColor } from '../../core/hooks/useBusinessColor'
import MenuLayout from '../../layout/MenuLayout'
import MenuHomePage from './MenuHomePage'
import MenuAboutPage from './MenuAboutPage'
import { Card, CardContent } from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import { ShoppingCart, Clock, Search } from 'lucide-react'
import { useTranslation } from '../../core/hooks/useTranslation'

export default function MenuPage() {
  const { slug } = useParams()
  const { isRTL } = useTranslation()
  const { color, darker, opacity15 } = useBusinessColor()
  const {
    isLoading,
    error,
    menu,
    fetchPublicMenu
  } = useMenuStore()
  const { setCurrentBusiness } = useCartStore()
  const fetchedSlugRef = useRef<string | null>(null)

  // Fetch data once when component mounts or slug changes
  useEffect(() => {
    if (slug && fetchedSlugRef.current !== slug) {
      fetchedSlugRef.current = slug
      fetchPublicMenu(slug)
      // Set current business in cart store
      setCurrentBusiness(slug)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug])

  if (!slug) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-stone-100 flex items-center justify-center p-4">
        <Card variant="elevated" className="max-w-md w-full animate-bounce-in border-stone-200 shadow-lg">
          <CardContent className="text-center py-10 sm:py-12">
            <div 
              className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg"
              style={{
                background: `linear-gradient(to bottom right, ${color}dd, ${darker})`,
                boxShadow: `0 10px 15px -3px ${opacity15}, 0 4px 6px -2px ${opacity15}`,
              }}
            >
              <Search className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-lg sm:text-xl font-bold text-stone-900 mb-2">
              {isRTL ? 'رابط غير صحيح' : 'Invalid Link'}
            </h2>
            <p className="text-stone-600 text-sm sm:text-base">
              {isRTL ? 'يرجى التأكد من صحة رابط القائمة' : 'Please check the menu link'}
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-stone-100 flex items-center justify-center p-4">
        <div className="text-center animate-fade-in">
          <div 
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 animate-pulse shadow-lg"
            style={{
              background: `linear-gradient(to bottom right, ${color}, ${darker})`,
              boxShadow: `0 10px 15px -3px ${opacity15}, 0 4px 6px -2px ${opacity15}`,
            }}
          >
            <ShoppingCart className="w-8 h-8 text-white" />
          </div>
          <div 
            className="loading-spinner w-12 h-12 mx-auto mb-4 border-3"
            style={{
              borderColor: opacity15,
              borderTopColor: color,
            }}
          ></div>
          <p className="text-stone-600 font-semibold text-sm sm:text-base">
            {isRTL ? 'جاري تحميل القائمة...' : 'Loading menu...'}
          </p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-stone-100 flex items-center justify-center p-4">
        <Card variant="elevated" className="max-w-md w-full animate-bounce-in border-stone-200 shadow-lg">
          <CardContent className="text-center py-10 sm:py-12">
            <div 
              className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg"
              style={{
                background: `linear-gradient(to bottom right, ${color}dd, ${darker})`,
                boxShadow: `0 10px 15px -3px ${opacity15}, 0 4px 6px -2px ${opacity15}`,
              }}
            >
              <Clock className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-lg sm:text-xl font-bold mb-2" style={{ color: darker }}>
              {isRTL ? 'خطأ في تحميل القائمة' : 'Error Loading Menu'}
            </h2>
            <p className="text-stone-600 mb-6 text-sm sm:text-base">{error}</p>
            <Button 
              onClick={() => slug && fetchPublicMenu(slug)}
              variant="primary"
              className="px-6 py-2.5"
            >
              {isRTL ? 'إعادة المحاولة' : 'Try Again'}
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!menu) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-stone-100 flex items-center justify-center p-4">
        <Card variant="elevated" className="max-w-md w-full animate-bounce-in border-stone-200 shadow-lg">
          <CardContent className="text-center py-10 sm:py-12">
            <div 
              className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg"
              style={{
                background: `linear-gradient(to bottom right, ${color}cc, ${color})`,
                boxShadow: `0 10px 15px -3px ${opacity15}, 0 4px 6px -2px ${opacity15}`,
              }}
            >
              <ShoppingCart className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-lg sm:text-xl font-bold text-stone-900 mb-2">
              {isRTL ? 'القائمة غير متوفرة' : 'Menu Not Available'}
              </h2>
              <p className="text-stone-600 text-sm sm:text-base">
                {isRTL ? 'لا توجد قائمة متاحة لهذا العمل التجاري حالياً' : 'No menu available for this business currently'}
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <MenuLayout slug={slug}>
      <Routes>
        <Route index element={<MenuHomePage />} />
        <Route path="about" element={<MenuAboutPage />} />
        <Route path="*" element={<Navigate to="." replace />} />
      </Routes>
    </MenuLayout>
  )
}
